import { deleteEntity, updateEntities } from '@redux/entities'
import { Schemas } from '@redux/schema'
import { normalize } from 'normalizr'

import api from '@functions/api'

import type { AgencyModel } from '@models/types'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'
import type { AppDispatch } from '@redux/store'

import type { CopyAgencyOptions, WatermarkImageParams } from '@hooks/useAgency'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/agency/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/agency/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/agency/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/agency/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/agency/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/agency/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/agency/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/agency/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/agency/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/agency/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/agency/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/agency/DELETE_FAILURE'

export type AgencyModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: AgencyModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadAgencies(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().agencies.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/agencies.json', options)
      .then(({ data }: { data: AgencyModel[] }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadAgency(agencyId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.agencies.loadedForKeys]
    const loadedIds = [...state.agencies.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (agencyId && !loadedIds.includes(agencyId)){
      loadedIds.push(agencyId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/agencies/${agencyId}.json`, options)
      .then(({ data }: { data: AgencyModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createAgency(agency: Partial<AgencyModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/agencies.json', options, config)
      .then(({ data }: { data: AgencyModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateAgency(
  agency: UpdateParams<AgencyModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      agency,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agencies/${agency.id}.json`, options, config)
      .then(({ data }: { data: AgencyModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function copyAgencyData(
  agencyParams: Partial<CopyAgencyOptions>,
  options: EntityOptions = {},
) {
  const { sourceAgencyId, destinationAgencyId, selectedEntityKeys } = agencyParams

  const config = {
    method: 'PUT',
    data: JSON.stringify({
      selected_entity_keys: selectedEntityKeys,
      source_agency_id: sourceAgencyId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agencies/${destinationAgencyId}/copy_data.json`, options, config)
      .then(({ data }: { data: { queued: boolean } }) => {
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateWatermarkImage(
  agency: UpdateParams<AgencyModel>,
  watermarkImage: WatermarkImageParams,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      id: agency.id,
      filename: watermarkImage.filename,
      watermark_image_file: watermarkImage.watermark_image_file,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agencies/${agency.id}/update_watermark_image.json`, options, config)
      .then(({ data }: { data: AgencyModel }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteAgency(agency: DeleteParams<AgencyModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/agencies/${agency.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.AGENCY)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function exportAgency(agency: Partial<AgencyModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agencies/${agency.id}/export.json`, options, config)
      .then(() => {
        dispatch(updateSuccess())

        return { success: true as const }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

export function generateSupportToken(agency: Partial<AgencyModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agencies/${agency.id}/generate_support_token.json`, options, config)
      .then(({ data }: { data: { token: string } }) => {
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateTemplatePermissions(
  agencyId: number,
  permissionAction: string,
  serviceId: number,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      permission_action: permissionAction,
      service_id: serviceId,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agencies/${agencyId}/permission_templates.json`, options, config)
      .then(({ data }: { data: {id: number} }) => {
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function toggleAgencyLock(agencyId: number, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/agencies/${agencyId}/toggle_agency_lock.json`, options, config)
      .then(({ data }: {data: Partial<AgencyModel>}) => {
        const normalizedJson = normalize(data, Schemas.AGENCY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadRelatedEntityCounts(options: EntityOptions = {}) {
  const { agencyId } = options

  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/agencies/${agencyId}/entity_counts.json`, options)
      .then(({ data }:{ data: { results: object } }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data: data.results }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Hydrates agency with Ably Payload
export function hydrateAgency(
  params: {
    agency: AgencyModel,
  },
) {
  const { agency } = params

  return (dispatch: AppDispatch) => {
    const normalizedJson = normalize(agency, Schemas.AGENCY)
    // @ts-expect-error TS2345
    dispatch(updateEntities(normalizedJson))
    dispatch(updateSuccess())
  }
}

// Reducer
export default function reducer(
  state: AgencyModuleState = initialState,
  action: Action = { type: '' },
): AgencyModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
